import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { readShared } from "@/lib/sharedLocation";
import { LEARN, SITES, SITE_MENUS, siteUrl } from "@/lib/sites";

/** The header's two navigation pieces, the same on the Shop, Care and
 *  Community sites:
 *  - SiteTabs: "Shop · Care · Community · Learn", a small switcher beside the logo.
 *  - MenuRow: the current site's own menu under the header (computers). On
 *    the shop each pet opens a panel of its categories and Brands opens the
 *    brands sold nearby. Items come from SITE_MENUS in lib/sites.js. */

// Colours per section: Shop orange, Care green, Community purple.
export const TONE = {
  shop: { text: "text-marigold", soft: "bg-marigold/15", pill: "bg-marigold/15 text-stoneWarm", line: "border-marigold", btn: "bg-marigold text-stoneWarm hover:bg-marigold/90" },
  care: { text: "text-teal", soft: "bg-teal/10", pill: "bg-teal/10 text-teal", line: "border-teal", btn: "bg-teal text-white hover:bg-teal/90" },
  community: { text: "text-violet-600", soft: "bg-violet-100", pill: "bg-violet-100 text-violet-700", line: "border-violet-500", btn: "bg-violet-600 text-white hover:bg-violet-700" },
};

/** A link to a page on this site (router) or another PawBazaar site. */
export function SiteLink({ item, current, className, children, onClick, ...rest }) {
  if (item.site === current) return <Link to={item.path} className={className} onClick={onClick} {...rest}>{children}</Link>;
  return <a href={`${siteUrl(item.site)}${item.path}`} className={className} onClick={onClick} {...rest}>{children}</a>;
}

let catsPromise = null;
export function useCategories() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    catsPromise = catsPromise || api.get("/categories").then(({ data }) => data || [], () => { catsPromise = null; return []; });
    let alive = true;
    catsPromise.then((d) => alive && setCats(d));
    return () => { alive = false; };
  }, []);
  return cats;
}

/** Shop · Care · Community · Learn beside the logo; the current one is white. */
export function SiteTabs({ current, className = "" }) {
  return (
    <nav aria-label="PawBazaar sites" className={`items-center gap-0.5 rounded-full bg-stone-100 p-1 ${className}`} data-testid="site-tabs">
      {[...SITES, LEARN].map((s) => (s.key === current
        ? <span key={s.key} aria-current="page" className="px-3 py-1 rounded-full bg-white text-sm font-bold text-stoneWarm shadow-sm">{s.label}</span>
        : <a key={s.key} href={s.url} className="px-3 py-1 rounded-full text-sm font-semibold text-stone-600 hover:text-stoneWarm" data-testid={`tab-${s.key}`}>{s.label}</a>))}
    </nav>
  );
}

/** Whether a menu item is the page being shown (its path and query match). */
function isHere(item, current, location) {
  if (item.site !== current) return false;
  const [path, query = ""] = item.path.split("?");
  if (path !== location.pathname) return false;
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(location.search);
  if (!query) return [...have.keys()].length === 0 || path !== "/shop";
  return [...want.entries()].every(([k, v]) => have.get(k) === v);
}

let careMenu = null;
/** On the Care site: the admin's list of care services, in its order. */
function useCareMenu(on) {
  const [groups, setGroups] = useState(null);
  useEffect(() => {
    if (!on) return undefined;
    careMenu = careMenu || api.get("/care/service-groups").then(({ data }) => data || [], () => { careMenu = null; return null; });
    let alive = true;
    careMenu.then((d) => alive && setGroups(d));
    return () => { alive = false; };
  }, [on]);
  return groups;
}

/** The current site's menu row, under the header on computers. */
export function MenuRow({ current }) {
  const careGroups = useCareMenu(current === "care");
  const base = SITE_MENUS[current] || [];
  const items = careGroups?.length
    ? [...careGroups.map((g) => ({ label: g.name, site: "care", path: `/services?group=${g.key}`, group: g.key })), ...base.filter((it) => !it.group)]
    : base;
  const t = TONE[current];
  const location = useLocation();
  const [open, setOpen] = useState(null);
  const timer = useRef(null);
  const box = useRef(null);
  useEffect(() => { setOpen(null); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === "Escape" && setOpen(null);
    const out = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(null); };
    document.addEventListener("keydown", esc);
    document.addEventListener("mousedown", out);
    return () => { document.removeEventListener("keydown", esc); document.removeEventListener("mousedown", out); };
  }, [open]);
  const hover = (key) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(key), key ? 140 : 220); };
  if (!items.length) return null;
  const openItem = items.find((it) => it.label === open);
  return (
    <div ref={box} className="hidden md:block border-t border-stone-100" onMouseLeave={() => hover(null)} data-testid="menu-row">
      <nav aria-label={`${SITES.find((s) => s.key === current)?.label} menu`} className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-1 overflow-x-auto [scrollbar-width:none]">
        {items.map((it) => {
          const here = isHere(it, current, location);
          const panel = it.pet || it.brands;
          const cls = `shrink-0 inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold border-b-[3px] -mb-px transition-colors ${here || open === it.label ? t.line : "border-transparent"} ${it.deal ? "text-terracotta" : "text-stoneWarm"} hover:text-stoneWarm`;
          return (
            <span key={it.label} className={`flex items-center ${it.gap ? "ml-3 pl-3 border-l border-stone-200" : ""}`}>
              {panel ? (
                <button type="button" className={cls} aria-expanded={open === it.label} aria-haspopup="true" data-testid={`menu-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onMouseEnter={() => hover(it.label)} onClick={() => { clearTimeout(timer.current); setOpen(open === it.label ? null : it.label); }}>
                  {it.label} <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${open === it.label ? "rotate-180" : ""}`} />
                </button>
              ) : (
                <SiteLink item={it} current={current} className={cls} aria-current={here ? "page" : undefined}
                  data-testid={`menu-${it.label.toLowerCase().replace(/\s+/g, "-")}`} onMouseEnter={() => hover(null)}>{it.label}</SiteLink>
              )}
            </span>
          );
        })}
      </nav>
      {openItem && (
        <div className="absolute left-0 right-0 top-full z-50 px-4 sm:px-6" onMouseEnter={() => hover(openItem.label)} data-testid="menu-panel">
          <div className="max-w-7xl mx-auto mt-1 rounded-3xl border border-stone-200 bg-white shadow-xl p-6">
            {openItem.pet ? <PetPanel item={openItem} current={current} close={() => setOpen(null)} />
              : <BrandsPanel current={current} close={() => setOpen(null)} />}
          </div>
        </div>
      )}
    </div>
  );
}

const MAX_GROUPS = 4;
const MAX_SUBS = 5;

/** How many products each category has for one pet ({own, all}, backend
 *  /categories/for-pet), fetched once per pet. */
const petCounts = {};
export function usePetCounts(pet) {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (!pet) return undefined;
    petCounts[pet] = petCounts[pet] || api.get("/categories/for-pet", { params: { pet_type: pet } })
      .then(({ data }) => data || { own: {}, all: {} }, () => { delete petCounts[pet]; return null; });
    let alive = true;
    petCounts[pet].then((d) => alive && setC(d));
    return () => { alive = false; };
  }, [pet]);
  return c;
}

/** One pet's categories: only those with products for this pet, the busiest
 *  first (products made for the pet count before those for all pets). Up to
 *  MAX_GROUPS main categories with up to MAX_SUBS sub-categories each, then
 *  links to the rest, so nothing is cut off. */
function PetPanel({ item, current, close }) {
  const cats = useCategories();
  const counts = usePetCounts(item.pet);
  const has = (c) => !counts || (counts.all?.[c.name] || 0) > 0;
  const busiest = (a, b) => (counts?.own?.[b.name] || 0) - (counts?.own?.[a.name] || 0)
    || (counts?.all?.[b.name] || 0) - (counts?.all?.[a.name] || 0) || a.name.localeCompare(b.name);
  const tops = cats.filter((c) => !c.parent_id && has(c)).sort(busiest);
  const kids = (id) => cats.filter((c) => c.parent_id === id && has(c)).sort(busiest);
  const link = (cat) => ({ site: "shop", path: `/shop?pet_type=${encodeURIComponent(item.pet)}${cat ? `&category=${encodeURIComponent(cat)}` : ""}` });
  const shown = tops.slice(0, MAX_GROUPS);
  const more = tops.slice(MAX_GROUPS);
  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: `repeat(${Math.max(1, shown.length)}, minmax(0, 1fr)) 16rem` }} data-testid="pet-panel">
      {shown.map((top) => {
        const subs = kids(top.id);
        return (
          <div key={top.id} className="min-w-0">
            <SiteLink item={link(top.name)} current={current} onClick={close} className="text-sm font-bold hover:text-marigold">{top.name}</SiteLink>
            <ul className="mt-2 space-y-1">
              {subs.slice(0, MAX_SUBS).map((sub) => (
                <li key={sub.id}><SiteLink item={link(sub.name)} current={current} onClick={close} className="text-sm text-stone-600 hover:text-stoneWarm hover:underline">{sub.name}</SiteLink></li>
              ))}
              {subs.length > MAX_SUBS && (
                <li><SiteLink item={link(top.name)} current={current} onClick={close} className="text-sm font-semibold text-stoneWarm hover:underline" data-testid="pet-panel-see-all">
                  See all {top.name.toLowerCase()}</SiteLink></li>)}
            </ul>
          </div>
        );
      })}
      {!shown.length && <SiteLink item={link()} current={current} onClick={close} className="text-sm font-semibold">All {item.label.toLowerCase()} products</SiteLink>}
      <div className="rounded-2xl bg-marigold/15 p-4 flex flex-col gap-2 self-start">
        <span className="text-xs font-bold uppercase tracking-widest text-terracotta">Offers</span>
        <span className="font-heading text-lg font-semibold leading-tight">Deals on {item.label.toLowerCase()} products near you</span>
        <SiteLink item={{ site: "shop", path: `/shop?offers=1&pet_type=${encodeURIComponent(item.pet)}` }} current={current} onClick={close}
          className="inline-flex items-center gap-1 text-sm font-semibold underline">See offers <ArrowRight className="h-4 w-4" /></SiteLink>
        <SiteLink item={link()} current={current} onClick={close} className="text-sm font-semibold text-stone-600 hover:underline">All {item.label.toLowerCase()} products</SiteLink>
        {more.length > 0 && (
          <div className="pt-2 mt-1 border-t border-marigold/30" data-testid="pet-panel-more">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">More</span>
            <ul className="mt-1 space-y-1">{more.map((c) => (
              <li key={c.id}><SiteLink item={link(c.name)} current={current} onClick={close} className="text-sm text-stone-700 hover:underline">{c.name}</SiteLink></li>))}</ul>
          </div>)}
      </div>
    </div>
  );
}

/** Brands sold by shops near the visitor (the place chosen on any site). */
function BrandsPanel({ current, close }) {
  const [brands, setBrands] = useState(null);
  useEffect(() => {
    const loc = readShared() || {};
    const params = { page: 1, per_page: 6, ...(loc.lat != null ? { lat: loc.lat, lng: loc.lng } : {}), ...(loc.pincode ? { pincode: loc.pincode } : {}) };
    api.get("/products", { params }).then(({ data }) => setBrands(data?.brands || []), () => setBrands([]));
  }, []);
  if (brands === null) return <p className="text-sm text-stone-500">Loading brands…</p>;
  if (!brands.length) return <p className="text-sm text-stone-600">No brands near you yet. Try another location.</p>;
  return (
    <div>
      <div className="text-sm font-bold mb-3">Brands sold near you</div>
      <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1.5">
        {brands.slice(0, 36).map((b) => (
          <li key={b} className="min-w-0"><SiteLink item={{ site: "shop", path: `/shop?brand=${encodeURIComponent(b)}` }} current={current} onClick={close}
            className="block truncate text-sm text-stone-700 hover:text-stoneWarm hover:underline">{b}</SiteLink></li>
        ))}
      </ul>
    </div>
  );
}
